import { c as _c } from "react/compiler-runtime";
import { Slot } from '@radix-ui/react-slot';
import type { ComponentProps, ReactElement } from 'react';
import { cn } from '#cn';
export type BadgeVariant = 'neutral' | 'success' | 'warning' | 'danger';

// Every variant carries a 1px border, even neutral's transparent one — so
// mixing variants in the same UI never causes a subtle size/vertical-align
// difference between a bordered and a border-less pill (Copilot review, #516).
const variantClasses: Record<BadgeVariant, string> = {
  neutral: 'border-transparent bg-surface-muted text-fg-muted',
  success: 'border-success/20 bg-success/10 text-success',
  warning: 'border-warning/20 bg-warning/10 text-warning',
  danger: 'border-danger/20 bg-danger/10 text-danger'
};
type BadgeProps = ComponentProps<'span'> & {
  variant?: BadgeVariant;
  asChild?: boolean;
};

/**
 * Generated with `pnpm dlx shadcn@latest add badge` and retokenized, matching
 * `table.tsx`: the CLI's raw output (this package has `cssVariables: false`)
 * emits literal `oklch(...)` colours for its `default`/`secondary`/
 * `destructive`/`outline` variants that don't exist anywhere else in this
 * codebase (no dark mode, no primary/secondary tokens), so those were
 * replaced with the role/status variants this app actually themes –
 * `success`/`warning`/`danger` via the `--color-success`/`--color-warning`/
 * `--color-danger` tokens, `neutral` as the transparent-border default. Ports
 * the existing "Coming soon" pill's look (`ComingSoonBadge`: `border-success/20
 * bg-success/10 text-success`) into a reusable variant for the
 * Users-management table.
 */
export function Badge(t0) {
  const $ = _c(12);
  let className;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      className,
      variant: t1,
      asChild: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
  }
  const variant = t1 === undefined ? "neutral" : t1;
  const asChild = t2 === undefined ? false : t2;
  const Component = asChild ? Slot : "span";
  const t3 = variantClasses[variant];
  let t4;
  if ($[5] !== className || $[6] !== t3) {
    t4 = cn("inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-3xs font-bold uppercase tracking-wider transition-colors [&>svg]:pointer-events-none [&>svg]:size-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-jaaq-black aria-invalid:border-danger", t3, className);
    $[5] = className;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== Component || $[9] !== props || $[10] !== t4) {
    t5 = <Component data-slot="badge" className={t4} {...props} />;
    $[8] = Component;
    $[9] = props;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}